"use client";

import { AlertTriangle, X, ShoppingCart, Mail, Loader2 } from "lucide-react";

/**
 * After-gen "Send to Email" stock-warning popup (PLA2-344).
 *
 * The consumer fork after a design is generated is Purchase vs. Send-to-Email.
 * Choosing "Send to Email" first surfaces this two-path popup: plant availability
 * is only locked at checkout, so emailing a design to buy later risks items
 * selling out. The user can divert to **Proceed to Purchase** (lock stock now) or
 * **Send Anyway** (email the design as-is).
 *
 * Stock freshness (D-4 / PLA2-330) isn't wired yet; `lastSyncedLabel` is an
 * optional hook for that copy once an `inventory_synced_at` exists.
 */
interface StockWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedToPurchase: () => void;
  onSendAnyway: () => void;
  isSending?: boolean;
  lastSyncedLabel?: string;
}

export function StockWarningModal({
  isOpen,
  onClose,
  onProceedToPurchase,
  onSendAnyway,
  isSending = false,
  lastSyncedLabel,
}: StockWarningModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={isSending ? undefined : onClose}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-warning-title"
        className="relative w-full max-w-md rounded-2xl bg-white dark:bg-plantista-900 shadow-2xl border border-gray-200 dark:border-plantista-800 animate-in zoom-in-95 duration-200"
      >
        <button
          onClick={onClose}
          disabled={isSending}
          className="absolute right-4 top-4 p-1.5 text-gray-400 hover:text-plantista-950 dark:hover:text-white transition disabled:opacity-40"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 mb-4">
            <AlertTriangle className="w-6 h-6" />
          </div>

          <h2
            id="stock-warning-title"
            className="text-xl font-bold font-display text-plantista-950 dark:text-white mb-2"
          >
            Plant stock can change
          </h2>
          <p className="text-sm text-plantista-600 dark:text-plantista-300 leading-relaxed">
            We confirm live availability the moment you check out. If you email
            this design to buy later, some plants may sell out before you come
            back. Purchasing now locks in your plants.
          </p>
          {lastSyncedLabel && (
            <p className="mt-2 text-xs text-plantista-500 dark:text-plantista-400">
              {lastSyncedLabel}
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={onProceedToPurchase}
              disabled={isSending}
              className="w-full py-3 bg-plantista-500 dark:bg-plantista-400 text-plantista-950 font-bold rounded-lg hover:bg-plantista-600 dark:hover:bg-plantista-300 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-5 h-5" />
              Proceed to Purchase
            </button>
            <button
              onClick={onSendAnyway}
              disabled={isSending}
              className="w-full py-3 bg-white dark:bg-plantista-800 text-plantista-950 dark:text-plantista-100 font-bold rounded-lg border border-gray-200 dark:border-plantista-700 hover:bg-gray-50 dark:hover:bg-plantista-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send Anyway
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
